import {createSelector, createSelectorCreator, defaultMemoize} from "reselect";
import isEqual from 'lodash/isEqual'; // for deep equality
import {CONSTANT} from "../constant";

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

// This activity list use for select activity
export const activityTypeFromProps = () => createSelector(
    state => state,
    (_, props) => props,
    (projects, props) => {
        let activities = props ? projects[props]?.taskActivityTypeList : []
        activities = activities?.map(a => {
            a.label = a.name;
            a.value = a._id;
            a.isActivated = a?.isActivated ?? true;
            return a;
        })?.filter(e => e.isActivated);
        return activities
    }
)
export const priority = () => createSelector(
    state => state?.projects,
    (_, props) => props,
    (projects, props) => {
        const a = projects?.[props?.project] ? projects?.[props?.project]?.priorityList?.find(e => e._id === props?.priority) : null
        return a ? a.name : '';
    }
)
export const selectedProjectPriorityList = () => createSelector(
    project => project,
    (project) => {
        const priorityList = [];
        if (project && project?.priorityList) {
            project?.priorityList?.forEach(e => priorityList?.push({...e, label: e?.name, value: e?._id}));
        }
        return priorityList;
    }
);
export const selectedProjectPriorityObj = () => createSelector(
    project => project,
    (project) => {
        const priorityObj = {};
        if (project && project?.priorityList) {
            project?.priorityList?.forEach(e => priorityObj[e?._id] = {...e, label: e?.name, value: e?._id});
        }
        return priorityObj;
    }
);
export const selectedProjectSectionObj = () => createSelector(
    project => project,
    (project) => {
        const sectionObj = {};
        if (project && project?.sectionList) {
            project?.sectionList?.forEach(e => sectionObj[e?._id] = {...e, label: e?.name, value: e?._id});
        }
        return sectionObj;
    }
);
export const activityType = () => createSelector(
    state => state?.projects,
    (_, props) => props,
    (projects, props) => {
        const a = projects?.[props?.project] ? projects[props?.project]?.taskActivityTypeList?.find(e => e._id === props?.activityType) : null
        return a ? a.name : '';
    }
)
export const filterSelector = {
    deepStored: () => {
        return createDeepEqualSelector(
            state => state?.filter,
            state => state?.storedFilter,
            (_, key) => key,
            (filter, storedFilter, key) => ({...filter[key], ...storedFilter[key]})
        )
    },
    stored: () => {
        return createSelector(
            state => state?.filter,
            state => state?.storedFilter,
            (_, key) => key,
            (filter, storedFilter, key) => ({...filter[key], ...storedFilter[key]})
        )
    },
    storedLeave: () => createSelector(
        state => state?.leave,
        state => state?.storedFilter,
        (_, key) => key,
        (filter, storedFilter, key) => ({...filter[key], ...storedFilter[key]})
    ),
    deepLeaveStored: () => {
        return createDeepEqualSelector(
            state => state?.leave,
            state => state?.storedFilter,
            (_, key) => key,
            (filter, storedFilter, key) => ({...filter[key], ...storedFilter[key]})
        )
    },
}
export const ticketSelector = {
    categoryList: () => createSelector(
        state => state.hrms.ticketCategory ?? [],
        (categoryList) => {
            const category = []
            if (categoryList?.length !== 0) categoryList.forEach(c => category.push({label: c.name, value: c._id, ...c}))
            return category
        }
    ),
    priorityList: () => createSelector(
        state => state.hrms.ticketPriority ?? [],
        (ticketPriority) => {
            const priority = []
            if (ticketPriority?.length !== 0) ticketPriority.sort((a, b) => parseFloat(a?.sortOrder) - parseFloat(b?.sortOrder))?.forEach(p => priority.push({label: p.name, value: p._id, ...p}))
            return priority
        }
    ),
    tagsList: () => createSelector(
        state => state.hrms.ticketTags ?? [],
        (ticketTags) => {
            const tags = []
            if (ticketTags?.length !== 0) ticketTags.forEach(t => tags.push({label: t.name, value: t._id, ...t}))
            return tags
        }
    ),
    statusList: () => createSelector(
        state => state.hrms.ticketStatus ?? [],
        (ticketStatus) => {
            const status = []
            if (ticketStatus?.length !== 0) ticketStatus?.sort((a, b) => parseFloat(a?.sortOrder) - parseFloat(b?.sortOrder))?.forEach(s => status.push({label: s.name, value: s._id, ...s}))
            return status
        }
    )
}

export const searchableContact = createSelector([_ => _, (_, c) => c], (contact, search) => {
    let str = search?.toLowerCase().trim()
    return contact.filter(data => {
        return data.name.toLowerCase().includes(str) || data.displayName?.toLowerCase().includes(str)
    })
});

// ===== PMS project/task selectors (migrated from pms/src/common/functions/reselector.jsx) =====
const getMembers = (project, users, _id) => {
    if (project) {
        const members = []
        // if (project.manager && users[project.manager] && !users[project.manager].isDeleted) members.push(users[project.manager])
        project.membersList?.forEach(m => {
            if(users[m.user] && !users[m.user].isDeleted) members.push(users[m.user])
        })
        members.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase()) ? 1 : -1)
        return members.filter((v, i, a) => a.findIndex(t => (t._id === v._id)) === i)
    } else return []
}
const getMembersUnassigned = (project, users, _id) => {
    if (project) {
        const members = []
        // if (project.manager && users[project.manager] && !users[project.manager].isDeleted) members.push(users[project.manager])
        project.membersList?.forEach(m => {
            if(users[m.user] && !users[m.user].isDeleted) members.push(users[m.user])
        })
        members.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase()) ? 1 : -1)
        members.unshift(CONSTANT.NULL_ASSIGN)
        return members.filter((v, i, a) => a.findIndex(t => (t._id === v._id)) === i)
    } else return []
}
export const userList = {
    activeWithOutClient: () => createSelector(
        state => state.pms.userList,
        (userList) => {
            if (userList?.length) return userList?.filter(u => !u?.isDeleted && u?.role !== "client");
            else return []
        }
    ),
    activeWithClient: () => createSelector(
        state => state.pms.userList,
        (userList) => {
            if (userList?.length) return userList?.filter(u => !u?.isDeleted);
            else return []
        }
    ),
    allWithOutClient: () => createSelector(
        state => state?.pms?.userList,
        (userList) => {
            if (userList?.length) return userList?.filter(u => u?.role !== "client");
            else return []
        }
    ),
    activeWithProjectClient: () => createSelector(
        state => state?.pms?.userList?.filter(u => !u?.isDeleted),
        (state, project) => state?.pms?.projects[project] ? state?.pms?.projects[project]?.membersList?.filter(m => m?.isClient)?.map(e => e?.user) : [],
        (userList, clientProjectMember) => {
            return userList?.filter(u => u.role === 'client' ? clientProjectMember?.includes(u?.value) : u);
        }
    ),
    allWithClientUACBase: () => createSelector(
        state => state?.pms?.userList?.filter(u => !u?.isDeleted),
        state => state?.common?.uac?.clientList?.modules?.clientList?.action?.read?.value,
        (state, project) => state?.pms?.projects[project] ? state?.pms?.projects[project]?.membersList?.filter(m => m?.isClient)?.map(e => e?.user) : [],
        (userList, uac, clientProjectMember) => {
            if (uac) return userList?.filter(u => u.role === 'client' ? clientProjectMember?.includes(u?.value) : u);
            else return userList?.filter(u => u.role !== 'client');
        }
    ),
    taskForm: () => createSelector(
        state => ((state.pms.taskForm && state.pms.projects[state.pms.taskForm.project]) || null),
        state => state.pms.users,
        state => state.common._id,
        getMembers
    ),
    activeProject: () => createSelector(
        state => ((state.pms.activeProject && state.pms.projects[state.pms.activeProject]) || null),
        state => state.pms.users,
        state => state.common._id,
        getMembers
    ),
    props: () => createSelector(
        state => state.pms.projects,
        state => state.pms.users,
        state => state.common._id,
        (_, props) => props,
        (projects, users, _id, project) => {
            if (projects[project]) {
                const members = []
                // if (projects[project].manager && users[projects[project].manager] && !users[projects[project].manager].isDeleted) members.push(users[projects[project].manager])
                projects[project]?.membersList?.forEach(m => {
                    if(users[m.user] && !users[m.user].isDeleted) members.push(users[m.user])
                })
                members.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase()) ? 1 : -1)
                return members.filter((v, i, a) => a.findIndex(t => (t._id === v._id)) === i)
            } else return []
        }
    ),
    // this user list for props project members with unassigned option
    propsUnAssigned: () => createSelector(
        (state, props) => state?.pms?.projects[props] ?? null,
        state => state.pms.users,
        state => state.common._id,
        getMembersUnassigned
    ),
    activeProjectUnAssigned: () => createSelector(
        state => ((state.pms.activeProject && state.pms.projects[state.pms.activeProject]) || null),
        state => state.pms.users,
        state => state.common._id,
        getMembersUnassigned
    ),
    myTasks: () => createSelector(
        state => state.pms.projects,
        state => state.pms.users,
        state => state.common._id,
        (_, props) => props,
        (projects, users, _id, projectId) => {
            const members = []
            // if (projects[projectId]?.manager && users[projects[projectId]?.manager] && !users[projects[projectId]?.manager]?.isDeleted) {
            //     if(projects[projectId]?.manager !== _id) members.push(users[projects[projectId]?.manager]);
            // }
            projects[projectId]?.membersList?.forEach(m => {
                if(users[m?.user] && !users[m?.user]?.isDeleted) members.push(users[m?.user])
            })
            members?.sort((a, b) => (a?.name?.toLowerCase() > b?.name?.toLowerCase()) ? 1 : -1)
            return members?.filter((v, i, a) => a?.findIndex(t => (t?._id === v?._id)) === i)
        }
    ),
    overdueTasks: () => createSelector(
        state => state.pms.projects,
        state => state.pms.users,
        state => state.common._id,
        (_, props) => props,
        (projects, users, _id, projectId) => {
            const members = []
            projects[projectId]?.membersList?.forEach(m => {
                if(users[m?.user] && !users[m?.user]?.isDeleted) members.push(users[m?.user])
            })
            members?.sort((a, b) => (a?.name?.toLowerCase() > b?.name?.toLowerCase()) ? 1 : -1)
            return members?.filter((v, i, a) => a?.findIndex(t => (t?._id === v?._id)) === i)
        }
    ),
    activeUACProject: () => createSelector(
        state => ((state.pms.activeProject && state.pms.projects[state.pms.activeProject]) || null),
        state => state.pms.users,
        (project, users) => {
            if (project) {
                const members = []
                if (project.manager && users[project.manager] && !users[project.manager].isDeleted) members.push(users[project.manager])
                project?.membersList?.forEach(m => {
                    if(users[m.user] && !users[m.user].isDeleted) members.push(users[m.user])
                })
                members.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase()) ? 1 : -1)
                return members.filter((v, i, a) => a.findIndex(t => (t._id === v._id)) === i)
            } else return []
        }
    ),
}
export const projectList = {
    withoutArchiveActiveProject: () => createSelector(
        state => state.pms.projectList.filter(p => (p._id !== state.pms.activeProject)),
        state => state.pms.projectStatusList.find(e => e.name === 'archive'),
        (projectList, archive) => {
            if (archive) return projectList.filter(p => (p.status !== archive._id))
            else return projectList
        }
    ),
    withoutArchive: () => createSelector(
        state => state.pms.projectList,
        state => state.pms.projectStatusList.find(e => e.name === 'archive'),
        (projectList, archive) => {
            if (archive) return projectList.filter(p => (p.status !== archive._id))
            else return projectList
        }
    ),
    withoutArchiveUserFilterProjects: () => createSelector(
        state => state.pms.projectList,
        state => state.pms.projectStatusList.find(e => e?.name === 'archive'),
        state => state.pms.myTasks?.filter?.assignTo,
        (projects, archive, assignTo) => {
            let projectLists = []
            if (archive) projects = projects?.filter(p => (p?.status !== archive._id && p?.isShownFromUAC !== true))
            if (!assignTo?.value) return projects // when not in my tasks
            projects?.map(e => {
                const index = e?.membersList?.findIndex(e => e?.user === assignTo?.value)
                if (index !== -1) projectLists?.push(e)
            })
            return projectLists
        }
    ),
    withoutArchiveActiveUACProject: () => createSelector(
        state => state.pms.projectList.filter(p => (p._id !== state.pms.activeProject)),
        state => state.pms.projectStatusList.find(e => e.name === 'archive'),
        (projectList, archive) => {
            if (archive) return projectList.filter(p => (p.status !== archive._id && p?.isShownFromUAC !== true))
            else return projectList
        }
    ),
    withoutArchiveUAC: () => createSelector(
        state => state.pms.projectList,
        state => state.pms.projectStatusList.find(e => e.name === 'archive'),
        (projectList, archive) => {
            if (archive) return projectList.filter(p => (p.status !== archive._id && p?.isShownFromUAC !== true))
            else return projectList
        }
    ),
    withoutUAC: () => createSelector(
        state => state.pms.projectList?.filter(p => !p?.isTemplate),
        (projectList) => {
            return projectList.filter(p => p?.isShownFromUAC !== true)
        }
    )
}
export const priorityList = {
    taskForm : () => createSelector(
        state => ((state.pms.taskForm && state.pms.projects[state.pms.taskForm.project]) || null),
        (project) => {
            const priorities = []
            if (project) project?.priorityList?.forEach(p => priorities.push({label: p.name, value: p._id, ...p}))
            return priorities
        }
    ),
    activeProject : () => createSelector(
        state => ((state.pms.activeProject && state.pms.projects[state.pms.activeProject]) || null),
        (project) => {
            const priorities = []
            if (project) project?.priorityList?.forEach(p => priorities.push({label: p.name, value: p._id, ...p}))
            return priorities
        }
    ),
    props : () => createSelector(
        state => state,
        (_, props) => props,
        (projects, props) => {
            const priorities = []
            if (projects[props]) projects?.[props]?.priorityList?.forEach(p => priorities.push({label: p.name, value: p._id, ...p}))
            return priorities
        }
    )
}

export const sectionListFromTaskForm = () => createSelector(
    state => ((state.pms.taskForm && state.pms.projects[state.pms.taskForm.project]) || null),
    state => state.common._id,
    (project, _id) => {
        const sections = []
        if (project) project?.sectionList?.forEach(e => sections.push({label: e.name, value: e._id}))
        return sections
    }
)
export const sectionListFromActiveProject = (ignoreNullSection = false) => createSelector(
    state => ((state.pms.activeProject && state.pms.projects[state.pms.activeProject]) || null),
    (project) => {
        const sections = []
        if (project) project?.sectionList?.forEach(s => sections.push({label: s.name, value: s._id, ...s}))
        return ignoreNullSection ? sections : [CONSTANT.NULL_SECTION, ...sections];
    }
)
export const sectionListFromProps = () => createSelector(
    state => state.pms.projects,
    (_, props) => props,
    (projects, project) => {
        const sections = []
        if (projects[project]) projects[project]?.sectionList?.forEach(s => sections.push({label: s.name, value: s._id, ...s}))
        return sections
    }
)

export const priorityListFromTaskForm = () => createSelector(
    state => ((state.pms.taskForm && state.pms.projects[state.pms.taskForm.project]) || null),
    (project) => {
        const priorities = []
        if (project) project?.priorityList?.forEach(p => priorities.push({label: p.name, value: p._id, ...p}))
        priorities.sort((a, b) => (a.sortOrder > b.sortOrder) ? 1 : -1) // https://pms.ubsapp.com/NLgyWdldGw/2/list/49E49xVLIR
        return priorities
    }
)
export const  priorityListFromActiveProject = (ignoreNullPriority = false) => createSelector(
    state => ((state.pms.activeProject && state.pms.projects[state.pms.activeProject]) || null),
    (project) => {
        const priorities = []
        if (project) {
            project?.priorityList?.forEach(p => {priorities.push({label: p?.name?.charAt(0)?.toUpperCase() + p?.name?.slice(1), value: p._id, ...p})});
        }
        // Append the "No Priority" option
        if (!ignoreNullPriority) priorities.push(CONSTANT.NULL_PRIORITY);

        // Return sorted list, including the "No Priority" option
        return priorities.sort((a, b) => {
            if (a.value === null) return -1; // "No Priority" should come first
            if (b.value === null) return 1;  // "No Priority" should come first
            return parseFloat(a.sortOrder) - parseFloat(b.sortOrder);
        });
    }
)
export const priorityListFromProps = () => createSelector(
    state => state.pms.projects,
    (_, props) => props,
    (projects,project) => {
        const priorities = []
        if (projects[project]) projects[project]?.priorityList?.forEach(p => priorities.push({label: p.name, value: p._id, ...p}))
        return priorities
    }
)

export const tagListFromTaskForm = () => createSelector(
    state => ((state.pms.taskForm && state.pms.projects[state.pms.taskForm.project]) || null),
    (project) => {
        const tags = []
        if (project) project?.tagList?.forEach(t => tags.push({label: t.name, value: t._id, ...t}))
        return tags
    }
)
export const mileStoneListFromTaskForm = () => createSelector(
    state => ((state.pms.taskForm && state.pms.projects[state.pms.taskForm.project]) || null),
    (project) => {
        const milestone = []
        if (project) project?.milestoneList?.forEach(m => {
            if (!m?.isCompleted) milestone.push({label: m.name, value: m._id, ...m})
        })
        return milestone
    }
)

export const milestoneListFromActiveProject = () => createSelector(
    state => ((state.pms.activeProject && state.pms.projects[state.pms.activeProject]) || null),
    (project) => {
        const milestone = []
        if (project) project?.milestoneList?.forEach(m => milestone.push({label: m.name, value: m._id, ...m}))
        return milestone
    }
)

export const taskStatusListFromTaskForm = () => createSelector(
    state => ((state?.pms?.taskForm && state?.pms?.projects[state?.pms?.taskForm?.project]) || null),
    (project) => {
        const taskStatus = []
        if (project) project?.taskStatusList?.forEach(s => taskStatus.push({label: s?.name, value: s?._id, ...s}))
        return taskStatus
    }
)

export const typeListFromTaskForm = () => createSelector(
    state => (((state.pms.taskForm || state.pms.newTask) && state.pms.projects[state.pms.taskForm.project || state.pms.newTask.project]) || null),
    (project) => {
        const tags = []
        if (project) project?.taskTypeList?.forEach(t => tags.push({label: t.name, value: t._id, ...t}))
        return tags
    }
)

export const tagListFromProps = () => createSelector(
    state => state,
    (_, props) => props,
    (projects, props) => {
        const tags = props ? projects[props]?.tagList || [] : []
        tags?.length && tags?.map(a => {
            a.label = a.name;
            a.value = a._id;
            return a;
        });
        return tags
    }
)

// This tag list use for select tag
export const tagListFromPropsIsActive = () => createSelector(
    state => state?.pms?.projects,
    (_, props) => props,
    (projects, props) => {
        let tags = props ? projects[props]?.tagList || [] : []
        tags = tags?.map(a => {
            a.label = a.name;
            a.value = a._id;
            a.isActivated = a?.isActivated ?? true;
            return a;
        })?.filter(e => e.isActivated);
        return tags
    }
)

// This tag list use for active project filter
export const tagListFromActiveProject = (ignoreNullTag = false) => createSelector(
    state => ((state.pms.activeProject && state.pms.projects[state.pms.activeProject]) || null),
    (project) => {
        const tags = [];
        if (project) {
            project?.tagList?.forEach(t => tags.push({label: t.name, value: t._id, ...t}));
        }
        // Prepend the "No Tags" option to the tags array
        return ignoreNullTag ? tags : [CONSTANT.NULL_TAGS, ...tags];
    }
);

export const typeListFromActiveProject = (ignoreNullTaskType = false) => createSelector(
    state => ((state.pms.activeProject && state.pms.projects[state.pms.activeProject]) || null),
    (project) => {
        const taskTypes = [];
        if (project) {
            project?.taskTypeList?.forEach(t => taskTypes.push({ label: t.name, value: t._id, ...t }));
        }
        // Prepend the "No Task Type" option to the task types array
        return ignoreNullTaskType ? taskTypes : [CONSTANT.NULL_TASK_TYPE, ...taskTypes];
    }
)

// This task type list use for select task type
export const typeListFromPropsIsActive = (ignoreNullTaskType = false) => createSelector(
    state => state?.pms?.projects,
    (_, props) => props,
    (projects, props) => {
        let taskType = projects?.[props]?.taskTypeList || [];
        taskType = taskType?.map(a => ({
            ...a,  // Spread operator to retain existing properties
            label: a.name,
            value: a._id,
            isActivated: a?.isActivated ?? true
        }))?.filter(e => e.isActivated);

        // Prepend the "No Task Type" option to the beginning of the list
        return ignoreNullTaskType ? taskType : [CONSTANT.NULL_TASK_TYPE, ...taskType];
    }
)

export const taskStatusListFromActiveProject = (ignoreNullTaskStatus = false) => createSelector(
    state => ((state.pms.activeProject && state.pms.projects[state.pms.activeProject]) || null),
    (project) => {
        const taskStatusList = [];
        if (project) {
            project?.taskStatusList?.forEach(t => taskStatusList.push({ label: t.name, value: t._id, ...t }));
        }
        // Prepend the "No Status" option to the task status array
        return ignoreNullTaskStatus ? taskStatusList : [CONSTANT.NULL_TASK_STATUS, ...taskStatusList];
    }
)

// This task status list use for select task status
export const taskStatusFromPropsIsActive = (ignoreNullTaskStatus = false) => createSelector(
    state => state?.pms?.projects,
    (_, props) => props,
    (projects, props) => {
        let taskStatus = projects[props] ? projects[props]?.taskStatusList : [];
        taskStatus = taskStatus?.map(a => ({
            ...a,  // Spread operator to retain existing properties
            label: a.name,
            value: a._id,
            isActivated: a?.isActivated ?? true
        }))?.filter(e => e.isActivated);

        // Prepend the "No Task Type" option to the beginning of the list
        return ignoreNullTaskStatus ? taskStatus : [CONSTANT.NULL_TASK_STATUS, ...taskStatus];
    }
)

// This milestone list use for select milestone
export const milestoneFromPropsIsActive = (ignoreNullMilestone = false) => createSelector(
    state => state?.pms?.projects,
    (_, props) => props,
    (projects, props) => {
        let milestone = props ? projects[props]?.milestoneList : [];
        milestone = milestone?.map(a => ({
            ...a,  // Spread operator to retain existing properties
            label: a.name,
            value: a._id,
            isActivated: a?.isActivated ?? true
        }))?.filter(e => e.isActivated);

        // Prepend the "No Task Type" option to the beginning of the list
        return ignoreNullMilestone ? milestone : [CONSTANT.NULL_MILESTONE, ...milestone];
    }
)

// This activity list use for select activity


// This activity list use for active project filter
export const activityListFromActiveProject = () => createSelector(
    state => ((state.pms.activeProject && state.pms.projects[state.pms.activeProject]) || null),
    (project) => {
        const activity = []
        if (project) project?.taskActivityTypeList?.forEach(p => activity.push({label: p.name, value: p._id, ...p}))
        return activity
    }
)

export const projectFromProps = () => createSelector(
    state => state,
    (_, props) => props,
    (projectList,projectId) => {

        const i = projectList.findIndex(e => e.projectNumber === parseInt(projectId))
        if (i > -1) return projectList[i];
        else return false
    }
)
export const projectIdFromProps = () => createSelector(
    state => state,
    (_, props) => props,
    (projectList,projectId) => {

        const i = projectList.findIndex(e => e.projectNumber === parseInt(projectId))
        if (i > -1) return projectList[i]._id;
        else return false
    }
)

const createInfoSelector = (getList, displayNameSelector) => () => createSelector(
    [getList, state => state.common.displayNameInApp],
    (user, dna) => {
        if (user) {
            const fl = `${user.firstName} ${user.lastName}`;
            const name = dna ? user.displayName : fl;
            return { name: name, firstName: dna ? name : user.firstName, image: user.profileImage };
        } else {
            return { name: '', image: '', firstName: '' };
        }
    }
);

const getUser = (state, id) => state.pms.userList.find(e => e._id === id);
const getClient = (state, id) => state.pms.companyList.find(e => e?._id === id);

export const getInfoSelector = createInfoSelector(getUser, state => state.common.displayNameInApp);
export const getClientInfoSelector = createInfoSelector(getClient, state => state.common.displayNameInApp);

// assign employee


// get label and value for employee and access level list
{/**
 type 1 for user,
 type 2 for access level
 type 4 for Line Manager
 type 5 for Line Manager 2
 type 6 for Reporting Manager
 **/}

// This client list for parent and sub client
export const getClientList = () => createSelector(
    clientList => clientList?.filter(c => !c?.isDeleted) || [],
    (_, props) => props,
    (clientList, flag) => {
        // flag false is active client
        // flag true is inactive client
        if (flag !== undefined) return clientList?.filter(c => c?.isDisable === flag);
        else return clientList
    }
);
export const getParentClientList = () => createSelector(
    state => ((state?.filter(c => !c?.isDeleted && !c?.parentCompany)) || []),
    (state) => state
)

export const getActiveUserAllClient = () => createSelector(
    state => state?.pms?.userList?.filter(c => !c?.isDeleted && c?.role !== 'client'),
    state => state?.pms?.companyList?.filter(_ => !_?.isDisable),
    (userList, clientList) => {
        return [...userList, ...clientList]
    }
)

export const getUserAllClient = () => createSelector(
    state => state?.pms?.userList, // include all active/inActive Users
    state => state?.pms?.companyList,
    (userList, clientList) => {
        return [...userList, ...clientList]
    }
)
